import React, { useMemo } from "react";
import { Circle, Marker } from "react-leaflet";
import L from "leaflet";
import { CustomMarkerProps, MarkerType } from "../../types/screensTypes";

/** Teardrop pin — matches experience-map-b .leaflet-marker-pin */
const createIcon = (color: string, position: number | string) =>
    new L.DivIcon({
        className: "leaflet-marker-pin-wrap",
        html: `<div class="leaflet-marker-pin" style="background:${color}"><span>${position}</span></div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
    });

const icons: Record<MarkerType, (position: number) => L.DivIcon> = {
    GARDEN: (position) => createIcon("#2e9b6f", position),
    INVISIBLE: (position) => createIcon("#8a93a0", position),
    MOBILE: (position) => createIcon("#1930d8", position),
    FIXED: (position) => createIcon("#1930d8", position),
    QUIZ: (position) => createIcon("#d4a017", position),
    SELECTED: (position) => createIcon("#d64545", position),
    STOP: (position) => createIcon("#d64545", position > 1000 ? position - 1000 : position),
};

const CustomMarker: React.FC<CustomMarkerProps> = ({
    id,
    lat = 0,
    lng = 0,
    position = 0,
    type = "INVISIBLE", // Default to a safe type
    selected = false,
    onSelect,
    onMarkerDragEnd,
    circleRadius = 0,
    circleColor = "0, 0, 255",
}) => {
    const location: [number, number] = useMemo(() => [lat || 0, lng || 0], [lat, lng]);

    const handleMarkerClick = () => onSelect?.(id);
    const handleMarkerDragEnd = (e: L.LeafletEvent) => {
        if (onMarkerDragEnd) {
            const newPosition = (e.target as L.Marker).getLatLng();
            onMarkerDragEnd(newPosition, id);
        }
    };

    // Memoize icon for performance
    const icon = useMemo(
        () => (selected ? icons["SELECTED"](position) : icons[type]?.(position) || icons["INVISIBLE"](position)),
        [selected, type, position],
    );

    return (
        <>
            <Marker
                key={id}
                position={location}
                icon={icon}
                eventHandlers={{ click: handleMarkerClick, dragend: handleMarkerDragEnd }}
                draggable={true}
            />
            <Circle
                center={location}
                radius={circleRadius}
                pathOptions={{
                    color: `rgba(${circleColor}, 0.5)`,
                    fillColor: `rgba(${circleColor}, 0.2)`,
                    fillOpacity: 0.5,
                }}
            />
        </>
    );
};

export default CustomMarker;
